import { useEffect, useRef, useState } from 'react';
import * as Styled from './styles';
import { useMap } from 'hooks/useMap';
import { useLayers } from 'hooks/useLayers';
import { IAtributo, ILayers } from 'contexts/@types/Layers';
import { FaRegSquare, FaCheckSquare } from "react-icons/fa";
import {
  RiArrowDownSFill,
  RiArrowUpSFill,
  RiSearchLine,
} from 'react-icons/ri';
import { transformExtent } from 'ol/proj';
import { ResizableBox } from 'react-resizable';
import { MdAddLink } from 'react-icons/md';
import { PiFileCsvBold, PiSortAscending, PiSortDescending } from 'react-icons/pi';

import { Geometry, LineString, MultiPolygon, Point, Circle as olCircle } from 'ol/geom';
import { Feature } from 'ol';
import GeoJSON from 'ol/format/GeoJSON';
import { Style, Circle, Fill } from 'ol/style';
import Stroke from 'ol/style/Stroke';
import VectorSource from 'ol/source/Vector';
import VectorLayer from 'ol/layer/Vector';
import { getFeatures } from 'services/featureRequest';
import loadingIcon from 'assets/icone/icone_loading.gif';
import WKT from 'ol/format/WKT';
import { shouldApplyNotranslate } from 'utils/shouldApplyNoTranslate';
import Polygon, { fromCircle } from 'ol/geom/Polygon';
import hash from 'object-hash';

import { CustomTooltip } from 'components/Tooltip';
import { Button } from 'components';
import { ModalFilters } from '../ModalFilters';
import { mergeCategorias } from 'utils/mergeCategorias';
import { createSearchResultLayer, createSearchResultPreference, parsePreference, SearchResultLayerPreference, buildQueryLabel } from 'utils/mapPreferences';

import { FaAnglesLeft, FaAnglesRight } from "react-icons/fa6";
import { ICamada, IData, IFeatures, IGeom, IRow, ISort } from 'contexts/@types/TabelaAtributos';
import { useTabelaAtributos } from 'hooks/useTabelaAtributos';
import { getVectorLayerAttributes } from 'utils/vectorLayerAttributes';
import { toast } from 'react-toastify';
import { useZIndex } from 'hooks/useZIndex';
import { captureMonitoringEvent } from 'services/monitoring';
import { filterAnalytesAttributes } from 'utils/analytes';

export function TabelaAtributos() {
  const LAYER_TITLE_MAX_LENGTH = 40;
  const { map, drawings, addedLayers, handleApplyLayerFilter } = useMap();
  const { setCategoryList } = useLayers();
  const { bringToFront, getZIndex } = useZIndex();

  const {
    open,
    setOpen,
    filter,
    setFilter,
    loading,
    setLoading,

    openFilter,
    setOpenFilter,

    getCurrentTab,
    tabs,
    setTabs,
    rows,
    setRows,
    currentPage,
    setCurrentPage,
    currentTab,
    setCurrentTab,

    selected,
    setSelected,
    selectAll,
    setSelectAll,
  } = useTabelaAtributos();

  const [sort, setSort] = useState<ISort[]>([]);

  const { getLayerAttritubeById, layersActived } = useLayers();
  const [isOverflowing, setIsOverflowing] = useState<boolean>(false);
  const [filtersByLayer, setFiltersByLayer] = useState<Record<string, string | null>>({});

  const changingTab = useRef(false);

  const [error, setError] = useState<boolean>(false);

  const vectorLayerRef = useRef<VectorLayer<Feature<Geometry>>>();
  const controllerRef = useRef<AbortController>(new AbortController());

  const filterRef = useRef<string | null>(null);

  function getLayerUuid(layer: ILayers) {
    return `${layer.layertitle}${layer.layer_filter_id || ''}`;
  }

  function getFilterForTab(tabIndex: number): string | undefined {
    const tab = tabs[tabIndex];
    if (!tab?.layer) return undefined;
    return filtersByLayer[getLayerUuid(tab.layer as ILayers)] ?? undefined;
  }

  useEffect(() => {
    filterRef.current = filter;
  }, [filter])
  
  useEffect(() => {
    if (!map) return;

    const vectorSource = new VectorSource();

    const vectorLayer = new VectorLayer({
      source: vectorSource,
      zIndex: 999,
    });

    vectorLayer.set("id", "overlaySelected");
    map.addLayer(vectorLayer);

    vectorLayerRef.current = vectorLayer;
  }, [map]);
  
  useEffect(() => {
    if (open) {
      setupTabs();
    }
    getTabValue(undefined, 0, true);
    setupTabs();
    setRows({ values: [], totalElements: 0 });
    setCurrentPage(0);
    setCurrentTab(0);
  }, [layersActived, addedLayers]);

  useEffect(() => {
    drawGeometry();
  }, [selected]);

  useEffect(() => {
    if (changingTab.current) changingTab.current = false;
    else getTabValue(undefined, undefined, true);
  }, [filter])
  
  function handleChangeTab(index: number) {
    if (index === currentTab) return;
    changingTab.current = true;
    setRows({ values: [], totalElements: 0 });
    setCurrentTab(index);
  }

  useEffect(() => {
    setSort([]);
    const tabLayer = tabs[currentTab]?.layer as ILayers | undefined;
    const storedFilter = tabLayer ? (filtersByLayer[getLayerUuid(tabLayer)] ?? null) : null;
    setFilter(storedFilter);
    filterRef.current = storedFilter;
    setSelected([]);
    setSelectAll(false);
    setLoading(true);
    getTabValue(currentTab, 0, true);
  }, [currentTab])

  //Resolve o erro na renderização da tabela de atributos após troca de camadas - issue 664
  useEffect(() => {
    if (!tabs.length) return;
    setRows({ values: [], totalElements: 0 });
    setCurrentPage(0);
    setCurrentTab(0);
    getTabValue(0, 0, true);
  }, [tabs]);

  function setupTabs() {
    const allLayers = [...layersActived, ...addedLayers];
    const uniqueLayers = allLayers.filter(
      (layer, index, self) =>
        self.findIndex(l =>
          l.layername === layer.layername &&
          l.layer_filter_id === layer.layer_filter_id &&
          l.layertitle === layer.layertitle
        ) === index
    );

    setTabs([]);
    uniqueLayers.forEach((layer: ILayers) => {
      const isExternalLayer = !!layer.is_vector && !!layer.vector_source;
      let headers: IAtributo[] = [];

      if (isExternalLayer) {
        headers = getExternalHeaders(layer);
      } else {
        try {
          const layerAttributes = getLayerAttritubeById(layer.id);
          headers = layerAttributes?.atributos ?? [];
        } catch (error) {
          headers = [];
        }
      }

      // tem o mesmo titulo mas sao camadas diferentes no geoserver...
      const sameTitleLayers = uniqueLayers.filter(l =>
        l.layertitle === layer.layertitle &&
        l.layername !== layer.layername
      );
      let uniqueTitle = layer.layertitle;

      if (sameTitleLayers.length >= 1 && layer.metadados?.subtema) {
        uniqueTitle = `${layer.layertitle} - ${layer.metadados.subtema}`;
      }
      else if (layer.layer_alias) {
        uniqueTitle = layer.layer_alias;
      }

      const visibleHeaders = headers.filter(
        attribute => attribute.visivel === true && attribute.nm_coluna !== 'ds_processo_grp_min',
      );
      const tableHeaders = filterAnalytesAttributes(visibleHeaders);

      setTabs(tab => [
        ...tab.filter(t => t.layerTitle !== layer.layertitle),
        {
          layerName: layer.layername,
          layerTitle: uniqueTitle,
          headers: tableHeaders,
          layer_query_filter: layer.layer_query_filter,
          host_url: layer.host_url,
          geomName: headers.find(a => a.ds_tipo === 'Geometria')?.nm_coluna || 'geom',
          layer: layer
        },
      ]);
    });
  }
  function getExternalHeaders(layer: ILayers): IAtributo[] {
    return getVectorLayerAttributes(layer);
  }
  const getGeom = (geom: Geometry) => { 
    if (geom instanceof olCircle) return fromCircle(geom);

    return geom;
  }

  function getTabValue(tab: number = currentTab, page: number = currentPage, reset = false) {
    if (!map) return;
    if (!getCurrentTab()) return;
    if (!tabs[tab]) return;

    const isExternalTab = !!tabs[tab].layer?.is_vector && !!tabs[tab].layer?.vector_source;
    if (isExternalTab) {
      getExternalTabValue(tab, page, reset);
      return;
    }

    controllerRef.current.abort();
    controllerRef.current = new AbortController();
    
    const controller = controllerRef.current;

    const { layerName, layer_query_filter, headers, host_url, geomName } = tabs[tab];

    const bbox = transformExtent(map.getView().calculateExtent(map.getSize()), 'EPSG:3857', 'EPSG:4326').join(',');
    const wktFormat = new WKT();

    const intersects = drawings.map(draw => `INTERSECTS(${geomName}, ${wktFormat.writeGeometry(getGeom(draw.geom))})`).join(' OR ');
    
    if (reset) setCurrentPage(0);

    getFeatures<IFeatures>(
      host_url,
      layerName,
      bbox,
      getSort(changingTab.current),
      reset? 0 : page * 500,
      geomName,
      controller,
      layer_query_filter,
      getFilterForTab(tab) ? getFilterForTab(tab) : undefined,
      intersects,
    )
    .then((html) => {
      if (html.data.toString().includes('<ServiceException code="InvalidParameterValue" locator="typeName">')) {
        setCurrentPage(page);
        setLoading(false);
        setError(false)
        setRows({ totalElements: 0, values: [] });
        return
      }

      const newValues = html.data.features.map((f: any) => ({
        id: hash(f.properties),
        data: clearData(f.properties, headers),
        geom: f.geometry,
      }));

      setRows(prev => ({
        values: reset
          ? newValues
          : [...prev.values, ...newValues],
        totalElements: html.data.totalFeatures,
      }));

      setCurrentPage(page);
      setLoading(false);
      setError(false);
      scrollRequestedRef.current = false;
      changingTab.current = false;
    })
    .catch((err) => {
      if (err?.name === 'CanceledError') return;

      toast.error('Erro ao carregar valores da tabela de atributos')
      setLoading(false);
      setError(true);
      setRows({totalElements: 0, values: []});
      setCurrentPage(page);
    });
  }

  function getExternalTabValue(tab: number = currentTab, page: number = currentPage, reset = false) {
    const tabData = tabs[tab];
    const layer = tabData.layer as ILayers;
    const source = layer.vector_source;
    if (!source) return;

    const features = source.getFeatures();
    const headers = tabData.headers;
    const currentFilter = getFilterForTab(tab);
    const filterValue = currentFilter?.trim().toLowerCase() ?? '';

    const filtered = filterValue
      ? features.filter(feature => {
        const props = { ...feature.getProperties() } as Record<string, any>;
        delete props.geometry;
        return Object.values(props).some(value =>
          String(value ?? '').toLowerCase().includes(filterValue)
        );
      })
      : features;

    const sorted = sortExternalFeatures(filtered, headers, tab);

    const pageIndex = reset ? 0 : page;
    const start = pageIndex * 500;
    const end = start + 500;
    const paged = sorted.slice(start, end);

    const formatter = new GeoJSON();

    const newValues = paged.map((feature) => {
      const props = { ...feature.getProperties() } as Record<string, any>;
      delete props.geometry;
      return {
        id: String(feature.getId() ?? hash(props)),
        data: clearData(props, headers),
        geom: formatter.writeGeometryObject(feature.getGeometry() as Geometry, {
          featureProjection: 'EPSG:3857',
          dataProjection: 'EPSG:3857',
        }) as IGeom,
      };
    });

    setRows(prev => ({
      values: reset ? newValues : [...prev.values, ...newValues],
      totalElements: filtered.length,
    }));

    setCurrentPage(pageIndex);
    setLoading(false);
    setError(false);
    scrollRequestedRef.current = false;
    changingTab.current = false;
  }

  function sortExternalFeatures(features: Feature<Geometry>[], headers: IAtributo[], tabIndex: number) {
    const sortConfig = getLocalSort(tabIndex, headers);
    if (sortConfig.length === 0) return features;

    return [...features].sort((a, b) => {
      const propsA = a.getProperties() as Record<string, any>;
      const propsB = b.getProperties() as Record<string, any>;

      for (const sortItem of sortConfig) {
        const aValue = propsA[sortItem.nm_coluna];
        const bValue = propsB[sortItem.nm_coluna];
        const comparison = compareValues(aValue, bValue, sortItem.order);
        if (comparison !== 0) return comparison;
      }
      return 0;
    });
  }

  function getLocalSort(tabIndex: number, headers: IAtributo[]) {
    if (headers.length === 0) return [];
    if (sort.length === 0 || tabIndex !== currentTab) {
      return [{ nm_coluna: headers[0].nm_coluna, order: 'A' }];
    }
    return sort;
  }

  function compareValues(aValue: any, bValue: any, order: string) {
    const direction = order === 'D' ? -1 : 1;
    if (aValue === null || aValue === undefined) return bValue === null || bValue === undefined ? 0 : 1 * direction;
    if (bValue === null || bValue === undefined) return -1 * direction;

    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return (aValue - bValue) * direction;
    }

    const aDate = parseDateValue(aValue);
    const bDate = parseDateValue(bValue);
    if (aDate && bDate) {
      return (aDate.getTime() - bDate.getTime()) * direction;
    }

    return String(aValue).localeCompare(String(bValue), 'pt-BR', { numeric: true }) * direction;
  }

  function parseDateValue(value: any): Date | null {
    if (value instanceof Date) return value;
    if (typeof value !== 'string') return null;
    const trimmed = value.trim();
    if (!trimmed) return null;
    const isDate = /^\d{4}-\d{2}-\d{2}/.test(trimmed) || /^\d{2}\/\d{2}\/\d{4}$/.test(trimmed);
    if (!isDate) return null;
    const timestamp = Date.parse(trimmed);
    if (Number.isNaN(timestamp)) return null;
    return new Date(timestamp);
  }

  useEffect(() => {
    if (rows.values.length === 0) {
      setError(true);
    }
    else {
      setError(false);
    }
  }, [rows])

  async function getAllTabValue(tab: number = currentTab) {
    if (!map) return;

    if (!tabs[tab]) return;
    setLoading(true);

    const { layerName, layer_query_filter, headers, host_url, geomName } = tabs[tab];
    const isExternalTab = !!tabs[tab].layer?.is_vector && !!tabs[tab].layer?.vector_source;

    if (isExternalTab) {
      const layer = tabs[tab].layer as ILayers;
      const source = layer.vector_source;
      if (!source) return;
      const formatter = new GeoJSON();

      const features = source.getFeatures();
      const values = features.map((feature) => {
        const props = { ...feature.getProperties() } as Record<string, any>;
        delete props.geometry;
        return {
          id: String(feature.getId() ?? hash(props)),
          data: clearData(props, headers),
          geom: formatter.writeGeometryObject(feature.getGeometry() as Geometry, {
            featureProjection: 'EPSG:3857',
            dataProjection: 'EPSG:3857',
          }),
        };
      });

      setLoading(false);
      setError(values.length === 0);
      setCurrentTab(tab);
      return {
        values,
        totalElements: values.length,
      };
    }

    const bbox = transformExtent(map.getView().calculateExtent(map.getSize()), 'EPSG:3857', 'EPSG:4326').join(',');
    const wktFormat = new WKT();

    const intersects = drawings.map(draw => `INTERSECTS(${geomName}, ${wktFormat.writeGeometry(draw.geom)})`).join(' OR ');
    try {
      const html = await getFeatures<IFeatures>(
        host_url,
        layerName,
        bbox,
        getSort(changingTab.current),
        null,
        geomName,
        undefined,
        layer_query_filter,
        getFilterForTab(tab) ?? undefined,
        intersects,
        true
      )

      setCurrentTab(tab);
      setLoading(false);
      setError(false);

      return {
        // eslint-disable-next-line
        values: html.data.features.map((f: any, index: number) => ({id: f.id, data: clearData(f.properties, headers), geom: f.geometry})),
        totalElements: html.data.totalFeatures,
      } 
    } catch (error) {
      // TODO disparar toast de erro
      setLoading(false);
      setError(true);
      setRows({totalElements: 0, values: []});
      setCurrentTab(tab);
    }
  }
  
  const enableLoad = () => {
    setLoading(true);
  };

  const handleMoveEnd = () => {
    getTabValue(undefined, 0, true);
  };

  useEffect(() => {
    if (!map) return;

    map.on('movestart', enableLoad);
    map.on('moveend', handleMoveEnd);

    return () => {
      map.un('movestart', enableLoad);
      map.un('moveend', handleMoveEnd);
    };
  }, [map, tabs, currentTab, drawings]);
  
  useEffect(() => {
    return () => {
      deleteOverlay();
    }
  }, [])

  useEffect(() => {
    getTabValue();
  }, [drawings]);

  useEffect(() => {
    if (sort.length !== 0) {
      getTabValue(undefined, undefined, true);
    }
  }, [sort]);

  // eslint-disable-next-line
  function clearData(properties: any, atributos: IAtributo[]) {
    const row: IData[] = [];
    atributos.map((attr: IAtributo) => {
      let value: any;
      let url = false;
      const rawValue = properties ? properties[attr.nm_coluna] : undefined;
      const hasValue = rawValue !== null && rawValue !== undefined && String(rawValue).trim() !== '';

      if (hasValue) {
        switch (attr.ds_tipo) {
          case 'Texto': {
            const textValue = typeof rawValue === 'object' ? JSON.stringify(rawValue) : String(rawValue);
            if (isValidURL(textValue)) {
              value = textValue;
              url = true;
            } else {
              value = textValue;
            }
            break;
          }
          case 'N\u00famero':
          case 'Numero': {
            const numValue = typeof rawValue === 'number'
              ? rawValue
              : Number(String(rawValue).replace(',', '.'));
            value = Number.isNaN(numValue)
              ? String(rawValue)
              : numValue.toLocaleString('pt-BR');
            break;
          }
          case 'Data':
            value = formatDate(rawValue);
            break;
          default:
            value = String(rawValue);
            break;
        }
      } else {
        value = 'N\u00e3o informado';
      }
      row.push({ cell: String(value), skipTranslate: shouldApplyNotranslate(attr), url: url });
    });
    return row;
  }

  function cleanOverlay() {
    vectorLayerRef.current?.getSource()?.clear()
  }

  function deleteOverlay() {
    if (!map) return;

    const layer = map.getLayers().getArray().find(l => 
      l.get("id") === "overlaySelected"
    );

    layer && map.removeLayer(layer);
  }

  function drawGeometry() {
    const source = vectorLayerRef.current?.getSource();
    if (!source) return;

    cleanOverlay();

    selected.forEach((selectedRow: IRow) => {
      const geometry = selectedRow.geom;
      if (geometry.type === 'Point' || geometry.type === 'MultiPoint') {
        const dotFeature = new Feature({
          geometry: new Point(geometry.type === 'Point' ? geometry.coordinates : geometry.coordinates[0]),
        });

        dotFeature.setStyle(
          new Style({
            image: new Circle({
              radius: 6,
              fill: new Fill({ color: '#65F057' }),
              stroke: new Stroke({
                color: '#65F057',
                width: 3,
              }),
            }),
          }),
        );
        source.addFeature(dotFeature);
      } else {
        if (geometry.type === 'MultiPolygon') {
          // eslint-disable-next-line
          geometry.coordinates.forEach((feat: any) => {
            const lineFeature = new Feature({
              geometry: new LineString(feat[0]),
            });

            lineFeature.setStyle(
              new Style({
                stroke: new Stroke({
                  color: '#65F057',
                  width: 5,
                }),
              }),
            );

            source.addFeature(lineFeature);
          });
        } else {
          const lineFeature = new Feature({
            geometry: new LineString(geometry.coordinates[0]),
          });

          lineFeature.setStyle(
            new Style({
              stroke: new Stroke({
                color: '#65F057',
                width: 5,
              }),
            }),
          );
          source.addFeature(lineFeature);
        }
      }
    });
  }

  function zoomToCoordinate(row: IRow) {
    if (!map) return;

    selectRow(row);

    const geometry = row.geom;
    
    if (geometry.type === 'Point' || geometry.type === 'MultiPoint') {
      const coord = geometry.type === 'Point'
      ? geometry.coordinates
      : geometry.coordinates[0];
      
      map.getView().setCenter(coord);
      if ((map.getView().getZoom() ?? 10) < 10) {
        map.getView().setZoom(10);
      }
    } else {
      // Create an OpenLayers geometry from your raw coords
      let olGeom;
      if (geometry.type === 'MultiPolygon') {
        olGeom = new MultiPolygon(geometry.coordinates);
      } else {
        olGeom = new Polygon(geometry.coordinates);
      }
      
      map.getView().fit(olGeom.getExtent(), {
        padding: [50, 500, 50, 500],
      });
    }
  }

  function isValidURL(url: string): boolean {
    const urlPattern = new RegExp(/^(http:\/\/|https:\/\/|www\.)/);

    return !!urlPattern.test(url);
  }

  function formatDate(dateString: any): string {
    const date = new Date(dateString);

    if (Number.isNaN(date.getTime())) return String(dateString);

    const day = date.getUTCDate();
    const month = date.getUTCMonth() + 1;
    const year = date.getUTCFullYear();

    const dayString = day < 10 ? `0${day}` : day.toString();
    const monthString = month < 10 ? `0${month}` : month.toString();

    return `${dayString}/${monthString}/${year}`;
  }

  function changeSort(header: IAtributo) {
    const value: ISort | undefined = sort.find(i => i.nm_coluna === header.nm_coluna);

    if (value) {
      if (value.order === 'D') {
        setSort(i => i.filter(ii => ii.nm_coluna !== header.nm_coluna));
      } else {
        setSort(i => i.map(ii => (ii.nm_coluna === header.nm_coluna ? { ...ii, order: 'D' } : ii)));
      }
    } else {
      setSort(i => [...i, { nm_coluna: header.nm_coluna, order: 'A' }]);
    }
  }

  function getSort(reset: boolean): ISort[] {
    if (getCurrentTab().headers.length === 0) return [];
    if (sort.length === 0 || reset) {
      return [{ nm_coluna: getCurrentTab().headers[0].nm_coluna, order: 'A' }];
    }
    return sort;
  }

  function getOrder(tab: IAtributo) {
    const value: ISort | undefined = sort.find(i => i.nm_coluna === tab.nm_coluna);

    if (value) {
      const Icon = value.order == 'A' ? PiSortDescending  : PiSortAscending;

      return (
        <Styled.ContentTitleColumn>
          <Icon size={15}/>
          <p>{value.order == 'A' ? "asc" : "desc"}</p>
        </Styled.ContentTitleColumn>
      )
    }
    return null;
  }

  const handleOpen = () => {
    if (rows.values.length === 0 && !open) {
      setLoading(true);
      getTabValue();
    }
    setOpen(o => !o);
  };

  function toggleSelectRow(row: IRow) {
    setSelectAll(false);
    if (!selected.some(s => s.id === row.id)) {
      return setSelected(selected => [...selected, row]);
    }
    return setSelected(selected => selected.filter(s => s.id !== row.id));
  }

  function selectRow(row: IRow) {
    setSelectAll(false);
    if (!selected.some(s => s.id === row.id)) {
      return setSelected(selected => [...selected, row]);
    }
  }

  function toggleSelectAll() {
    if (selectAll) {
      setSelected([]);
    }
    else {
      setSelected(rows.values);
    }
    setSelectAll(s => !s)
  }

  async function gerarCsv() {
    const data = await getAllTabValue() as {
      values: {
          id: any;
          data: IData[];
          geom: any;
      }[];
      totalElements: number;
    };
    
    // Convert the data array into a CSV string
    const csvString = [
      getCurrentTab().headers.map(i => (i.nm_alias ? i.nm_alias : i.nm_coluna)),
      ...data.values.map(row => row.data.map(data => {
        let str = String(data.cell);
        if (str.includes('"')) str = str.replace(/"/g, '""'); // escapa aspas
        if (str.includes(',') || str.includes('"') || str.includes('\n')) {
          return `"${str}"`; // envolve em aspas se tiver vírgula, aspas ou quebra de linha
        }
        return str;
      })),
    ]
      .map(row => row.join(','))
      .join('\n');

    const blob = new Blob(['\uFEFF' + csvString], { type: 'text/csv;charset=utf-8;' });

    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = getCurrentTab().layerTitle || 'download.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    const currentLayer = getCurrentTab().layer;
    void captureMonitoringEvent({
      tipo_evento: 'download',
      identificador_camada: currentLayer?.layername || getCurrentTab().layerName,
    });
  }

  function truncateText(text: string, maxLength: number): string {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return `${text.substring(0, maxLength)}...`;
  }
  
  const addLayerToSearchResults = (camadaOriginal: ICamada, query: string): ICamada | null => {
    try {
      if (!query || query.trim().length === 0) return null;

      const categoriasStr = sessionStorage.getItem('pesquisas');
      const categorias = categoriasStr ? JSON.parse(categoriasStr) : [];

      const idCategoria = 1000;
      const idTema = 1000;
      const nmCategoria = "Resultados de pesquisas";
      const nmTema = "Pesquisas Recentes";

      let categoria = categorias.find((cat: any) => cat.id_categoria === idCategoria);
      if (!categoria) {
        categoria = {
          id_categoria: idCategoria,
          camadas_pesquisadas: [],
          nm_categoria: nmCategoria,
          ord_categoria: idCategoria,
          temas: []
        };
        categorias.push(categoria);
      }

      let tema = categoria.temas.find((t: any) => t.id_tema === idTema);
      if (!tema) {
        tema = {
          id_tema: idTema,
          nm_tema: nmTema,
          ord_tema: idTema,
          visivel: true,
          categoria: idCategoria,
          subtema: [],
          layers: []
        };
        categoria.temas.push(tema);
      }

      let camadaFiltrada = null;

      const queryLabel = buildQueryLabel(query);

      const baseLayerTitle = camadaOriginal.layer_alias || camadaOriginal.layertitle;

      let layerTitle = `${baseLayerTitle}_resultado_pesquisa ${queryLabel}`;

      const count = tema.layers.filter((o: any) => o.layertitle.startsWith(layerTitle)).length;

      if (count > 0) layerTitle += ` (${count})`

      camadaFiltrada = createSearchResultLayer(camadaOriginal as any, query, { layertitle: layerTitle });

      tema.layers.push(camadaFiltrada);

      const savedSearchResults = parsePreference<SearchResultLayerPreference[]>('searchResultLayers', []);
      localStorage.setItem(
        'searchResultLayers',
        JSON.stringify([...savedSearchResults, createSearchResultPreference(camadaOriginal as any, query, camadaFiltrada as any)]),
      );

      const jaExiste = categoria.camadas_pesquisadas.some(
        (l: any) => l.layertitle === camadaOriginal.layertitle
      );

      if (!jaExiste) {
        categoria.camadas_pesquisadas.push(camadaOriginal.layertitle);
      }
      sessionStorage.setItem('pesquisas', JSON.stringify(categorias));
      setCategoryList(prev => mergeCategorias(prev, categorias));
      return camadaFiltrada;
    } catch (err) {
      console.error('Erro ao adicionar camada em Resultados pesquisas:', err);
      return null;
    }
  }

  const tableContainerRef = useRef<HTMLDivElement>(null);

  const scrollRequestedRef = useRef<boolean>(false);

  useEffect(() => {
    if (open || loading) {
      bringToFront('map-attributes-table');
    }
  }, [open, loading, bringToFront]);

  useEffect(() => {
    const container = tableContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      if (loading) return;
      if (scrollRequestedRef.current) return;

      const nearBottom =
        container.scrollTop + container.clientHeight >= container.scrollHeight / 2;

      if (nearBottom && rows.values.length < rows.totalElements) {
        scrollRequestedRef.current = true;
        getTabValue(undefined, currentPage + 1)
      }
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [loading, rows, currentPage]);
  
  function onFilter(newFilter: string, layerUuid?: string) {
    const targetLayer = layerUuid
      ? tabs.find(t => getLayerUuid(t.layer as ILayers) === layerUuid)?.layer as ILayers | undefined
      : (getCurrentTab()?.layer as ILayers | undefined);
    const targetUuid = targetLayer ? getLayerUuid(targetLayer) : undefined;
    const currentAppliedFilter = targetUuid ? (filtersByLayer[targetUuid] ?? null) : null;
    const shouldCreateSearchResultLayer =
      !!newFilter && !!targetLayer && newFilter !== currentAppliedFilter;

    if (targetUuid) {
      setFiltersByLayer(prev => ({ ...prev, [targetUuid]: newFilter || null }));
      handleApplyLayerFilter(targetUuid, newFilter || null, targetLayer?.layer_query_filter ?? null);
    }

    if (shouldCreateSearchResultLayer) {
      addLayerToSearchResults(targetLayer as any, newFilter);
    }

    if (!targetUuid || targetUuid === getLayerUuid(getCurrentTab().layer as ILayers)) {
      if (newFilter === filter) setLoading(false);
      else setFilter(newFilter);
      return;
    }

    setLoading(false);
  }

  function scrollTabs(value: number) {
    const el = document.getElementById('tab-container');
    if (el) el.scrollLeft += value;
  }

  useEffect(() => {
    const el = document.getElementById('tab-container');
    el && setIsOverflowing(el.scrollWidth > el.clientWidth);
  }, [rows, open]);

  return (
    <>
      <Styled.Wrapper
        active={open || loading}
        zIndex={getZIndex('map-attributes-table')}
        onMouseDown={() => bringToFront('map-attributes-table')}
        onTouchStart={() => bringToFront('map-attributes-table')}
      >
        <Styled.OpenButton
          open={open}
          label={'Tabela(s) de atributos'}
          onClick={() => {
            bringToFront('map-attributes-table');
            handleOpen();
          }}
        >
          {open ? <RiArrowDownSFill /> : <RiArrowUpSFill />}
        </Styled.OpenButton>
        {open === true && (
          <ResizableBox width={Infinity} height={300} minConstraints={[Infinity, 200]} maxConstraints={[Infinity, window.innerHeight - 90]} resizeHandles={['n']} axis="y">
            <Styled.Box $loading={loading}>
              {loading && (
                <Styled.Loading>
                  <img src={loadingIcon} alt="Loading" />
                </Styled.Loading>
              )}
              {error && (
                <Styled.Error>
                  Nenhuma informação disponível para a camada selecionada.
                </Styled.Error>
              )}
              <div className='tab-navigation-container'>
                <div id='tab-container' className="tab-wrapper">
                  {tabs.map((t, index) => {
                    return (
                      <Styled.TabButton key={index} active={currentTab === index} onClick={() => handleChangeTab(index)}>
                        <CustomTooltip title={t.layerTitle}>
                          <span>{truncateText(t.layerTitle, LAYER_TITLE_MAX_LENGTH)}</span>
                        </CustomTooltip>
                      </Styled.TabButton>
                    );
                  })}
                </div>
                <div className='buttons-wrapper'>
                  { isOverflowing &&
                    <>
                      <Button
                        id='left'
                        className='clean'
                        onClick={() => scrollTabs(-200)}
                      >
                        <FaAnglesLeft />
                      </Button>
                      <Button
                        id='right'
                        className='clean'
                        onClick={() => scrollTabs(200)}
                      >
                        <FaAnglesRight />
                      </Button>
                    </>
                  }
                </div>
              </div>
              <Styled.TableContainer ref={tableContainerRef} $loading={loading}>
                {!!getCurrentTab() && (
                  <table>
                    <thead>
                      <tr>
                        <th className="small-cell">
                          <a style={{ display: 'flex', justifyContent: 'center', margin: '5px' }}>
                            {
                              selectAll? <FaCheckSquare title="Destacar todos" className="map-icon" onClick={() => toggleSelectAll()} />
                              : <FaRegSquare title="Destacar todos" className="map-icon" onClick={() => toggleSelectAll()} />
                            }
                            {!error && <PiFileCsvBold
                              title="Exportar CSV"
                              onClick={gerarCsv}
                              className="map-icon"
                            />}
                          </a>
                        </th>
                      {getCurrentTab().headers.map((header, index) => {
                        const order = getOrder(header);
                        const columnTitle = header.nm_alias ? header.nm_alias : header.nm_coluna;

                        return (
                          <Styled.TableThTitleColumn
                            key={index}
                            onClick={() => changeSort(header)}
                            $order={!!order}
                          >
                            <CustomTooltip
                              title={columnTitle}
                              placement="top"
                              disableInteractive
                            >
                              <div>
                                <p>{columnTitle}</p>
                                {order}
                              </div>
                            </CustomTooltip>
                          </Styled.TableThTitleColumn>
                        );
                      })}
                    </tr>
                    </thead>
                    <tbody key={currentTab}>
                      {rows.values.map((row, rIndex) => {
                        const Icon = selected.some(s => s.id === row.id) ? FaCheckSquare : FaRegSquare;

                        return <tr key={rIndex} onClick={() => toggleSelectRow(row)}>
                          <td className={selected.some(s => s.id === row.id) ? 'small-cell selected' : 'small-cell'}>
                            <a style={{ display: 'flex', justifyContent: 'center', margin: '5px' }}>
                              <Icon
                                title="Destacar"
                                className="map-icon"
                                onClick={(e) => {
                                  toggleSelectRow(row);
                                  e.stopPropagation();
                                  }
                                }
                              />
                              <RiSearchLine
                                title="Zoom para"
                                className="map-icon"
                                onClick={(e) => {
                                  zoomToCoordinate(row);
                                  e.stopPropagation();
                                }}
                              />
                            </a>
                          </td>
                          {row.data.map((cell, cIndex) => (
                            <td
                              className={
                                (cell.skipTranslate ? 'notranslate' : '') +
                                (selected.some(s => s.id === row.id) ? ' selected' : '')
                              }
                              key={`${rIndex}_${cIndex}`}
                              style={{width: 'auto'}}
                            >
                              {cell.url ? (
                                <a href={cell.cell} target="blank">
                                  Mais info <MdAddLink />
                                </a>
                              ) : (
                                <a onClick={(e) => e.stopPropagation()}>
                                  {cell.cell}
                                </a>
                              )}
                            </td>
                          ))}
                        </tr>
                      })}
                    </tbody>
                  </table>
                )}
              </Styled.TableContainer>
              <Styled.Footer>
                {!error && !loading && <>
                <div style={{marginLeft: 'auto'}}>{`${rows.totalElements} linhas`}</div>
                </>
                }
              </Styled.Footer>
            </Styled.Box>
          </ResizableBox>
        )}
      </Styled.Wrapper>
      { openFilter &&
        <ModalFilters
          rows={rows}
          onClose={() => {setOpenFilter(false)}}
          onSelect={() => setLoading(true)}
          onFilter={onFilter}
          appliedFilters={filtersByLayer}
        />
      }
    </>
  );
}

